"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import { ImageProps } from "next/image";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { cn } from "../lib/utils";

interface ImageWithCaptionProps extends Omit<ImageProps, "alt"> {
  alt: string;
  caption?: string;
  showCaption?: boolean;
  maxWidth?: number;
}

export default function ImageWithCaption({
  alt,
  caption,
  showCaption = true,
  className,
  style,
  src,
  maxWidth,
  ...props
}: ImageWithCaptionProps) {
  const [open, setOpen] = useState(false);
  const captionText = caption || alt;

  const getSrcString = useCallback(() => {
    if (typeof src === "string") return src;
    if (typeof src === "object" && "src" in src) return src.src;
    return "";
  }, [src]);
  const srcString = getSrcString();

  return (
    <>
      <figure
        className="my-8 text-center"
        data-llm-image-alt={alt}
        data-llm-image-caption={showCaption && captionText ? captionText : undefined}
        style={
          maxWidth
            ? {
                maxWidth: `${maxWidth}px`,
                marginLeft: "auto",
                marginRight: "auto",
              }
            : {}
        }
      >
        <Image
          src={src}
          alt={alt}
          className={cn(
            "max-w-full h-auto rounded-lg shadow-lg dark:shadow-2xl dark:shadow-gray-800 cursor-zoom-in",
            className,
          )}
          style={style}
          onClick={() => setOpen(true)}
          {...props}
        />
        {showCaption && captionText && (
          <figcaption className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic leading-6">
            {captionText}
          </figcaption>
        )}
      </figure>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-[100vw] max-h-[100dvh] w-screen h-[100dvh] p-0 sm:max-w-[100vw] bg-black/90 border-none shadow-none rounded-none"
          showCloseButton={false}
          onPointerDownOutside={(e) => e.preventDefault()}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{alt}</DialogTitle>
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
            aria-label="닫기"
          >
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <TransformWrapper
            initialScale={1}
            minScale={1}
            maxScale={5}
            doubleClick={{ mode: "toggle", step: 0.7 }}
            pinch={{ step: 20 }}
            wheel={{ step: 0.2 }}
            smooth
            centerOnInit
          >
            <TransformComponent
              wrapperStyle={{
                width: "100%",
                height: "100dvh",
              }}
              contentStyle={{
                width: "100%",
                height: "100dvh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={srcString}
                alt={alt}
                className="max-w-full max-h-[90dvh] object-contain select-none"
                draggable={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </DialogContent>
      </Dialog>
    </>
  );
}
